import { Table } from '@devexpress/dx-react-grid-material-ui';
import makeStyles from '@mui/styles/makeStyles';
import CellComponentStyles from '../Cell.styles';
import NRNoDataPresentComponent from '../../NRNoDataPresentComponent/NRNoDataPresentComponent';
import NRLoader from '../../NRLoader/NRLoader';
import { forwardRef, memo } from 'react';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/styles';
import { CONSTANTS } from '../../../constants/constants';
const useStyles = makeStyles(CellComponentStyles);

export const EMPTY_CELL_VALUE = '-';

export const NRHandleEmptyCellValue = forwardRef(({ value }, ref) => {
  const classes = useStyles();
  return (
    <span ref={ref} className={clsx({ [classes.emptyCell]: !value })}>
      {!!value ? value : EMPTY_CELL_VALUE}
    </span>
  );
});

export const CellComponent = props => {
  const theme = useTheme();
  const { column, value, row } = props;
  const classes = useStyles();
  const isPrimitiveValue = rawValue => ['string', 'number', 'boolean'].includes(typeof rawValue);
  const shouldUseCellAriaLabel = rawValue => {
    if (rawValue == null || rawValue === '') return true;
    return isPrimitiveValue(rawValue);
  };
  const normalizeValueForAria = (columnName, rawValue, rowValue) => {
    if (rawValue == null || rawValue === '') return EMPTY_CELL_VALUE;
    const stringValue = String(rawValue);

    // Prevent screen readers from interpreting semantic versions as dates (e.g. 4.19.98).
    if (columnName === 'version' || columnName === 'fixVersions') {
      return stringValue.replace(/\./g, ' dot ');
    }

    // Certificates Expiration column: announce the displayed bucket, not the ISO timestamp.
    if (columnName === 'expireDate') {
      const isExpiredFlag = !!rowValue?.expired;
      if (isExpiredFlag) return 'Expired';
      const expiryTime = new Date(rawValue).getTime();
      if (!Number.isFinite(expiryTime)) return 'Unknown';
      const nowTime = Date.now();
      const daysUntilExpiry = Math.floor(
        (expiryTime - nowTime) / CONSTANTS.TIME.MILLISECONDS_PER_DAY
      );
      if (daysUntilExpiry < 0) return 'Expired';
      return daysUntilExpiry <= 30 ? '< 30 Days' : '> 30 Days';
    }

    return stringValue;
  };
  const cellLabel = shouldUseCellAriaLabel(value)
    ? `${column?.title?.value || column?.name || 'Cell'}: ${normalizeValueForAria(column?.name, value, row)}`
    : undefined;
  if (!column.isLast) {
    return (
      <Table.Cell
        {...props}
        data-nr-col={column?.name}
        tabIndex={0}
        {...(cellLabel ? { 'aria-label': cellLabel } : {})}
        value={<NRHandleEmptyCellValue value={value} />}
        style={{
          ...props.style,
          borderLeft: 'unset',
          borderRight: 'unset',
          ...(column.name !== 'info'
            ? column.name === 'cve' ||
              (column.name === 'name' && Object.keys(row).includes('dependency')) ||
              (column.name === 'filePath' && Object.keys(row).includes('expireDate'))
              ? { padding: theme.spacing(0), position: 'relative' }
              : { padding: theme.spacing(1.25, 2) }
            : { padding: theme.spacing(0.5, 0) }),
          ...(!!column.actions && { textOverflow: 'unset' })
        }}
        className={classes.focusedBorder}
      />
    );
  }
  return (
    <Table.Cell
      {...props}
      data-nr-col={column?.name}
      tabIndex={0}
      {...(cellLabel ? { 'aria-label': cellLabel } : {})}
      style={{ ...props.style, padding: theme.spacing(1.25, 2), borderLeft: 'unset', borderRight: 'unset' }}
    >
      <NRHandleEmptyCellValue value={value} />
    </Table.Cell>
  );
};

const MemoizedNRLoader = memo(() => <NRLoader />);

export const NoDataCellComponent = memo(({ loading, noDataComponent, ...props }) => {
  const theme = useTheme();
  return (
    <Table.Cell {...props}>
      <Box
        role={!loading ? 'status' : undefined}
        aria-live={!loading ? 'polite' : undefined}
        aria-label={!loading ? 'No data available.' : undefined}
        tabIndex={!loading ? 0 : undefined}
        sx={{
          padding: theme.spacing(10, 0),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {!loading && <NRNoDataPresentComponent />}
        {!!loading && <MemoizedNRLoader />}
        {noDataComponent}
      </Box>
    </Table.Cell>
  );
});
